import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import QRCode from 'qrcode';
import { buildEventsApiUrl } from '../config/api';
import { buildAuthHeaders, buildJsonAuthHeaders } from '../utils/authFetch';

interface EventLocationRecord {
  id: string;
  eventId: string;
  name: string;
  capacity: number;
  active: boolean;
  qrTargetUrl?: string;
}

interface Props {
  eventId: string;
  eventName?: string;
  userId: string;
  disabled?: boolean;
  initialLocations?: EventLocationRecord[];
  onLocationsChanged?: (locations: EventLocationRecord[]) => void;
}

const EventLocationsManagerComponent: React.FC<Props> = ({ eventId, eventName, userId, disabled, initialLocations = [], onLocationsChanged }) => {
  const [locations, setLocations] = useState<EventLocationRecord[]>(initialLocations);
  const optimisticLocationIdsRef = React.useRef<Record<string, number>>({});
  const [loading, setLoading] = useState(initialLocations.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('4');
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [qrLocation, setQrLocation] = useState<EventLocationRecord | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const printableTitle = useMemo(() => {
    if (!qrLocation) return '';
    return `${eventName || 'Event'}\n${qrLocation.name}\nCapacity: ${qrLocation.capacity}`;
  }, [eventName, qrLocation]);

  const loadLocations = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/locations?userId=${encodeURIComponent(userId)}`), {
        headers: buildAuthHeaders(userId)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load locations');
      const fetchedLocations = Array.isArray(data.locations) ? data.locations : [];
      const now = Date.now();
      const optimisticEntries = Object.entries(optimisticLocationIdsRef.current).filter(([, expiresAt]) => expiresAt > now);
      optimisticLocationIdsRef.current = Object.fromEntries(optimisticEntries);

      setLocations((current) => {
        const optimisticMissing = current.filter((location) => optimisticLocationIdsRef.current[location.id] && !fetchedLocations.some((fetched: EventLocationRecord) => fetched.id === location.id));
        const nextLocations = [...fetchedLocations, ...optimisticMissing];
        const currentSerialized = JSON.stringify(current);
        const nextSerialized = JSON.stringify(nextLocations);
        if (currentSerialized === nextSerialized) return current;
        onLocationsChanged?.(nextLocations);
        return nextLocations;
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load locations');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [eventId, userId, onLocationsChanged]);

  useEffect(() => {
    if (!eventId || !userId) return;
    let cancelled = false;

    loadLocations(false);
    const intervalId = window.setInterval(() => {
      if (!cancelled) {
        loadLocations(true);
      }
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [eventId, userId, loadLocations]);

  useEffect(() => {
    if (initialLocations.length > 0) {
      setLocations(initialLocations);
      setError(null);
      setLoading(false);
    }
  }, [initialLocations]);

  useEffect(() => {
    const buildQr = async () => {
      if (!qrLocation?.qrTargetUrl) {
        setQrDataUrl(null);
        return;
      }
      const next = await QRCode.toDataURL(qrLocation.qrTargetUrl, {
        width: 320,
        margin: 2,
        color: { dark: '#111827', light: '#ffffff' },
      });
      setQrDataUrl(next);
    };
    buildQr().catch(() => setQrDataUrl(null));
  }, [qrLocation]);

  const resetForm = () => {
    setName('');
    setCapacity('4');
    setEditingLocationId(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = {
        userId,
        name: name.trim(),
        capacity: Number(capacity || 0),
        active: true,
      };
      if (!payload.name) throw new Error('Location name is required');
      if (!payload.capacity || payload.capacity < 1) throw new Error('Capacity must be at least 1');
      const url = editingLocationId
        ? buildEventsApiUrl(`/api/events/${eventId}/locations/${editingLocationId}`)
        : buildEventsApiUrl(`/api/events/${eventId}/locations`);
      const response = await fetch(url, {
        method: editingLocationId ? 'PUT' : 'POST',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save location');

      const savedLocation = data.location || data.eventLocation || null;
      if (savedLocation?.id) {
        optimisticLocationIdsRef.current[savedLocation.id] = Date.now() + 15000;
        setLocations((current) => {
          const nextLocations = editingLocationId
            ? current.map((location) => (location.id === savedLocation.id ? savedLocation : location))
            : [savedLocation, ...current.filter((location) => location.id !== savedLocation.id)];
          onLocationsChanged?.(nextLocations);
          return nextLocations;
        });
      }

      resetForm();
      window.setTimeout(() => {
        loadLocations(true);
      }, 1200);
    } catch (err: any) {
      setError(err.message || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (location: EventLocationRecord) => {
    setEditingLocationId(location.id);
    setName(location.name);
    setCapacity(String(location.capacity || 1));
  };

  const handleToggleActive = async (location: EventLocationRecord) => {
    try {
      setSaving(true);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/locations/${location.id}`), {
        method: 'PUT',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify({ userId, name: location.name, capacity: location.capacity, active: !location.active }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update location');
      await loadLocations();
    } catch (err: any) {
      setError(err.message || 'Failed to update location');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadQr = () => {
    if (!qrDataUrl || !qrLocation) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `${qrLocation.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-qr.png`;
    link.click();
  };

  const handlePrintableQr = () => {
    if (!qrDataUrl || !qrLocation) return;
    const escapedTitle = printableTitle.split('\n').join('<br/>');
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>${qrLocation.name}</title><style>body{font-family:Arial,sans-serif;padding:32px;text-align:center;color:#111827}.sheet{max-width:700px;margin:0 auto}img{width:360px;max-width:100%;height:auto}.title{font-size:28px;font-weight:700;margin-bottom:16px}.meta{font-size:16px;color:#4b5563;margin-top:16px}</style></head><body><div class="sheet"><div class="title">${escapedTitle}</div><img src="${qrDataUrl}" alt="${qrLocation.name}"/><div class="meta">Location QR for ${qrLocation.name}</div></div><script>window.addEventListener('load',()=>setTimeout(()=>window.print(),150));</script></body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const printUrl = URL.createObjectURL(blob);
    window.open(printUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(printUrl), 60000);
  };

  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', mb: 3, backgroundColor: '#f8f9fa' }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Event Locations</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: '#6c757d' }}>
        Define tables, stations, or areas for live trade sessions. Each location gets a printable QR code with a readable label.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField label="Location Name" value={name} onChange={(e) => setName(e.target.value)} disabled={disabled || saving} fullWidth />
        <TextField label="Capacity" type="number" value={capacity} onChange={(e) => setCapacity(e.target.value)} disabled={disabled || saving} sx={{ minWidth: 140 }} />
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={handleSave} disabled={disabled || saving}>{editingLocationId ? 'Update' : 'Add'}</Button>
          {editingLocationId && <Button variant="outlined" onClick={resetForm} disabled={saving}>Cancel</Button>}
        </Stack>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>
      ) : locations.length > 0 ? (
        <Stack spacing={1.5}>
          {locations.map((location) => (
            <Paper key={location.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{location.name}</Typography>
                    <Chip size="small" label={`Capacity: ${location.capacity}`} variant="outlined" />
                    <Chip size="small" label={location.active ? 'Active' : 'Inactive'} color={location.active ? 'success' : 'default'} />
                  </Stack>
                  {location.qrTargetUrl && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      QR URL: {location.qrTargetUrl}
                    </Typography>
                  )}
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="outlined" onClick={() => handleEdit(location)} disabled={saving || disabled}>Edit</Button>
                  <Button variant="outlined" onClick={() => setQrLocation(location)} disabled={saving}>QR Tools</Button>
                  <Button variant="outlined" color={location.active ? 'warning' : 'success'} onClick={() => handleToggleActive(location)} disabled={saving || disabled}>
                    {location.active ? 'Deactivate' : 'Activate'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Alert severity="info">No locations defined yet.</Alert>
      )}

      <Dialog open={Boolean(qrLocation)} onClose={() => setQrLocation(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Location QR Tools</DialogTitle>
        <DialogContent>
          {qrLocation && (
            <Stack spacing={2} alignItems="center" sx={{ pt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>{eventName || 'Event'} — {qrLocation.name}</Typography>
              <Typography variant="body2" color="text.secondary">Capacity: {qrLocation.capacity}</Typography>
              {qrDataUrl ? <Box component="img" src={qrDataUrl} alt={qrLocation.name} sx={{ width: 240, height: 240 }} /> : <CircularProgress size={28} />}
              {qrLocation.qrTargetUrl && <Link href={qrLocation.qrTargetUrl} target="_blank" rel="noreferrer">Open target URL</Link>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrLocation(null)}>Close</Button>
          <Button onClick={handlePrintableQr} disabled={!qrDataUrl}>Printable Version</Button>
          <Button variant="contained" onClick={handleDownloadQr} disabled={!qrDataUrl}>Download Image</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const EventLocationsManager = React.memo(EventLocationsManagerComponent);

export default EventLocationsManager;
